import { evaluationScore, isEvaluationPending, numericScore } from './evaluationStatus';

export const EVALUATION_SECTIONS = [
  { key: 'history', labelKey: 'historyTaking', max: 30 },
  { key: 'diagnosis', labelKey: 'diagnosis', max: 40 },
  { key: 'management', labelKey: 'management', max: 30 }
];

const object = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const text = value => typeof value === 'string' ? value.trim() : '';
const textList = value => (Array.isArray(value) ? value : [value]).map(text).filter(Boolean);

export const uniqueFeedback = values => {
  const seen = new Set();
  return textList(values).filter(value => {
    const key = value.normalize('NFKC').replace(/\s+/g, ' ').toLocaleLowerCase().replace(/[.!?。]+$/, '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Keep historical evidence intact; only the summary selects a few existing items.
export const auditTextList = value => (Array.isArray(value) ? value : [value])
  .filter(item => item !== null && item !== undefined && item !== '')
  .map(item => typeof item === 'string' ? item : JSON.stringify(item));

const interleave = lists => {
  const items = [];
  const length = Math.max(0, ...lists.map(list => list.length));
  for (let index = 0; index < length; index++) lists.forEach(list => { if (list[index]) items.push(list[index]); });
  return items;
};

export function getEvaluationPresentation(rawEvaluation, fallbackScore) {
  const evaluation = object(rawEvaluation);
  const breakdown = object(evaluation.breakdown);
  const checklist = object(evaluation.checklist);
  const concise = object(evaluation.concise_feedback);
  const pending = isEvaluationPending(evaluation, fallbackScore);
  const sections = EVALUATION_SECTIONS.map(config => {
    const detail = object(breakdown[config.key]);
    const candidateMax = numericScore(detail.max);
    const max = candidateMax !== null && candidateMax > 0 ? candidateMax : config.max;
    const rawScore = numericScore(detail.score);
    return {
      ...config, max, score: rawScore !== null && rawScore >= 0 && rawScore <= max ? rawScore : null,
      detail, items: Array.isArray(checklist[`${config.key}_items`]) ? checklist[`${config.key}_items`].map(object) : []
    };
  });
  const legacyStrengths = interleave(sections.map(section => textList(section.detail.achieved)));
  // Rubric labels avoid repeating old boilerplate guidance on every missed item.
  // Interleaving covers the three domains without inventing a clinical ranking.
  const legacyGaps = interleave(sections.map(section => [
    ...textList(section.detail.partial), ...textList(section.detail.missed)
  ]));
  const legacyImprovements = legacyGaps.length ? legacyGaps : [
    ...textList(evaluation.improvement_plan),
    ...interleave(sections.map(section => textList(section.detail.improvements)))
  ];
  const candidateSummary = text(concise.summary) || text(evaluation.feedback);
  return {
    pending,
    total: pending ? null : evaluationScore(evaluation, fallbackScore),
    sections,
    // Older narrative reports remain complete in details, never cut mid-advice.
    summary: candidateSummary.length <= 360 ? candidateSummary : '',
    pendingReason: text(concise.summary) || text(evaluation.review_reason),
    strengths: pending ? [] : uniqueFeedback(Array.isArray(concise.strengths) ? concise.strengths : legacyStrengths).slice(0, 2),
    improvements: pending ? [] : uniqueFeedback(Array.isArray(concise.improvements) ? concise.improvements : legacyImprovements).slice(0, 3),
    feedback: text(evaluation.feedback),
    improvementPlan: auditTextList(evaluation.improvement_plan),
    concise,
    evaluatorVersion: text(evaluation.evaluator_version),
    rubricVersion: text(evaluation.rubric_version)
  };
}

export function getInstructorReview(submission) {
  const sections = EVALUATION_SECTIONS.map(config => ({ ...config, score: numericScore(submission?.[`doctor_score_${config.key}`]) }));
  if (sections.some(section => section.score === null || section.score < 0 || section.score > section.max)) return null;
  return { sections, total: sections.reduce((sum, section) => sum + section.score, 0), comment: text(submission.doctor_comment) };
}
